import { IntlMessageFormat } from 'intl-messageformat';
import { parse } from '@formatjs/icu-messageformat-parser';
import memoize from '@formatjs/fast-memoize';
/**
 * Cached IntlMessageFormat AST objects for a string + locale
 *
 * Key is of format String + "_" + Locale. This does increase memory utilization because it'll cache
 * a new AST object for each string. As a result, we provide an option to disable this caching.
 *
 * Details on this technique can be found here: https://formatjs.io/docs/intl-messageformat/#passing-in-ast
 */
const formatAstCache = new Map();
/**
 * Intl Formatters are expensive to create.
 *
 * This allows the re-use of formatters across messages, rather than creating new ones each time.
 *
 * Details on this technique can be found here: https://formatjs.io/docs/intl-messageformat/#formatters
 */
const formatters = {
    getNumberFormat: memoize((locale, opts) => new Intl.NumberFormat(locale, opts)),
    getDateTimeFormat: memoize((locale, opts) => new Intl.DateTimeFormat(locale, opts)),
    getPluralRules: memoize((locale, opts) => new Intl.PluralRules(locale, opts))
};
function getStringPropertyImplementation(translatedString, locale, enableFormatCaching) {
    // We have two types of strings that we support:
    // 1. Strings with no placeholders - we don't need to parse these and can provide them as a string property for easy access.
    // 2. Strings with placeholder inputs - these need to be parsed and turned into a function that can be invoked with the corresponding placeholder values
    switch (translatedString.f) {
        case 0: {
            return translatedString.s;
        }
        case 1: {
            return (placeholders) => {
                const { s } = translatedString;
                if (!enableFormatCaching) {
                    return new IntlMessageFormat(s, locale, undefined, { formatters }).format(placeholders);
                }
                else {
                    const cacheKey = `${s}_${locale}`;
                    let messageFormatAst = formatAstCache.get(cacheKey);
                    if (!messageFormatAst) {
                        // Cache the AST of the message which is expensive and
                        // is redundant work for the translation of the same string.
                        messageFormatAst = parse(s);
                        formatAstCache.set(cacheKey, messageFormatAst);
                    }
                    return new IntlMessageFormat(messageFormatAst, locale, undefined, { formatters }).format(placeholders);
                }
            };
        }
    }
}
/**
 * Generates an object that can be used to generate user facing strings from an ICU string table
 * @param stringTableJson - A JavaScript object mapping unique keys to translated ICU strings
 * @param locale - The locale of the strings in the table
 * @param enableFormatCaching - Enabled by default. This will apply caching to speed up repeat string formatting. It can be optionally disabled to save memory.
 */
export function getStringProvider(stringTableJson, locale, enableFormatCaching = true) {
    const entries = Object.entries(stringTableJson.strings);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const result = {};
    entries.forEach(([key, value]) => {
        result[key] = getStringPropertyImplementation(value, locale, enableFormatCaching);
    });
    return result;
}
//# sourceMappingURL=getStringProvider.js.map